/**
 * main.js — FF LitB theme
 *
 * AOS, Odometer, and Splide are enqueued as separate vendor scripts in
 * enqueue.php with this file declared as a dependent. WordPress guarantees
 * they execute before this file, so globals are always available here.
 *
 * odometerOptions is output inline (before odometer.js) via wp_add_inline_script.
 * Don't set it here.
 */

document.addEventListener( 'DOMContentLoaded', () => {

	// ── AOS — Animate On Scroll ───────────────────────────────────────────────
	// HTML usage:  <div data-aos="fade-up" data-aos-delay="100">...</div>
	// All options: https://michalsnik.github.io/aos/

	if ( window.AOS && typeof window.AOS.init === 'function' ) {
		window.AOS.init( {
			duration: 700,
			easing:   'ease-out-quad',
			once:     true,   // animate only once — better performance
			offset:   80,     // px before element enters viewport
		} );
	}


	// ── Odometer — Animated number counters ──────────────────────────────────
	// HTML usage:  <span class="odometer" data-count="4200">0</span>
	//
	// Fires when element enters the viewport (IntersectionObserver).
	// odometerOptions already set inline before odometer.js loaded (see enqueue.php).

	const counters = document.querySelectorAll( '.odometer[data-count]' );

	if ( counters.length && window.Odometer ) {
		const odometerObserver = new IntersectionObserver(
			( entries ) => {
				entries.forEach( ( entry ) => {
					if ( ! entry.isIntersecting ) return;

					const el    = entry.target;
					const od    = new window.Odometer( { el, value: 0 } );
					const count = parseInt( el.dataset.count, 10 );

					// Small delay so the user sees it count up from zero
					setTimeout( () => od.update( count ), 150 );
					odometerObserver.unobserve( el );
				} );
			},
			{ threshold: 0.35 }
		);

		counters.forEach( ( el ) => odometerObserver.observe( el ) );
	}


	// ── Splide — Carousels ────────────────────────────────────────────────────
	// HTML usage:
	//   <div class="splide" data-splide='{"type":"loop","perPage":3}'>
	//     <div class="splide__track">
	//       <ul class="splide__list">
	//         <li class="splide__slide">...</li>
	//       </ul>
	//     </div>
	//   </div>
	//
	// All .splide elements on the page auto-mount.
	// Per-carousel options go in the data-splide attribute (JSON).
	// Full docs: https://splidejs.com/guides/options/

	if ( window.Splide ) {
		document.querySelectorAll( '.splide' ).forEach( ( el ) => {
			new window.Splide( el ).mount();
		} );
	}

	// Courses view toggle for archive + member course tabs.
	// Our custom toolbar can live outside the form, so we update the relevant
	// LearnDash list directly and persist the preference ourselves.
	const applyCourseListView = ( form, view ) => {
		if ( ! form ) {
			return false;
		}

		const courseLists = form.querySelectorAll( '.bb-course-items:not(.is-cover)' );
		if ( ! courseLists.length ) {
			return false;
		}

		const layoutShell = Array.from( form.children ).find(
			( child ) => child instanceof HTMLElement && (
				child.classList.contains( 'grid-view' ) ||
				child.classList.contains( 'list-view' ) ||
				child.classList.contains( 'bb-grid' ) ||
				child.classList.contains( 'bb-list' )
			)
		);

		if ( layoutShell ) {
			layoutShell.classList.remove( 'grid-view', 'bb-grid', 'list-view', 'bb-list' );
			layoutShell.classList.add( view === 'list' ? 'list-view' : 'grid-view' );
			layoutShell.classList.add( view === 'list' ? 'bb-list' : 'bb-grid' );
		}

		courseLists.forEach( ( list ) => {
			list.classList.remove( 'grid-view', 'bb-grid', 'list-view', 'bb-list' );
			list.classList.add( view === 'list' ? 'list-view' : 'grid-view' );
			list.classList.add( view === 'list' ? 'bb-list' : 'bb-grid' );
		} );

		return true;
	};

	const saveCourseListView = ( view, filters ) => {
		if (
			! filters ||
			! window.bs_data ||
			! window.bs_data.ajaxurl ||
			! window.bs_data.nonce_list_grid
		) {
			return;
		}

		const viewObject = filters.getAttribute( 'data-view' );
		if ( ! viewObject ) {
			return;
		}

		const body = new URLSearchParams( {
			action: 'buddyboss_lms_save_view',
			option: 'bb_layout_view',
			object: viewObject,
			type: view,
			nonce: window.bs_data.nonce_list_grid,
		} );

		window.fetch( window.bs_data.ajaxurl, {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
			},
			body: body.toString(),
		} ).catch( () => {} );
	};

	const showInlineMemberCourses = () => {
		const inlineShell = document.querySelector( '.ff-member-inline-shell[data-ff-inline-courses="true"]' );
		if ( ! inlineShell ) {
			return false;
		}

		const profilePanel = inlineShell.querySelector( '[data-ff-member-panel="profile"]' );
		const coursesPanel = inlineShell.querySelector( '[data-ff-member-panel="courses"]' );
		const toolbar = document.querySelector( '.ff-member-icon-nav[data-courses-title]' );
		const title = toolbar ? toolbar.querySelector( '.ff-courses-title' ) : null;

		if ( ! coursesPanel ) {
			return false;
		}

		inlineShell.classList.add( 'is-showing-courses' );

		if ( profilePanel ) {
			profilePanel.hidden = true;
		}

		coursesPanel.hidden = false;

		if ( title ) {
			title.textContent = toolbar.getAttribute( 'data-courses-title' ) || title.textContent;
		}

		return true;
	};

	document.addEventListener( 'click', ( event ) => {
		const toggle = event.target.closest( '.ff-courses-menu-view-filters .layout-view-course' );
		if ( ! toggle ) {
			return;
		}

		const view = toggle.getAttribute( 'data-view' ) === 'list' ? 'list' : 'grid';
		const filters = toggle.closest( '.ff-courses-menu-view-filters' );
		const form = toggle.closest( 'form.bb-courses-directory' ) ||
			document.querySelector( '#bb-learndash_profile form.bb-courses-directory, #bb-courses-directory-form.bb-courses-directory' );
		const targetUrl = toggle.getAttribute( 'href' );

		if ( filters ) {
			filters.querySelectorAll( '.layout-view-course' ).forEach( ( btn ) => {
				btn.classList.remove( 'active', 'is-active' );
			} );
			toggle.classList.add( 'active', 'is-active' );
		}

		saveCourseListView( view, filters );
		const showingInlineCourses = showInlineMemberCourses();

		if (
			showingInlineCourses &&
			targetUrl &&
			'#' !== targetUrl &&
			window.history &&
			typeof window.history.replaceState === 'function'
		) {
			window.history.replaceState( {}, document.title, targetUrl );
		}

		if ( form ) {
			event.preventDefault();
			event.stopImmediatePropagation();

			applyCourseListView( form, view );
			return;
		}

		if ( targetUrl && '#' !== targetUrl ) {
			event.preventDefault();
			event.stopImmediatePropagation();
			window.location.assign( targetUrl );
		}
	} );

	// BuddyBoss topic forms keep the submit button behind CSS state classes.
	// This fallback syncs title/content state and the hidden textarea value,
	// which is especially important on topic edit screens.
	const getForumEditorHtml = ( html ) => {
		if ( ! html ) {
			return '';
		}

		const fragment = document.createElement( 'div' );
		fragment.innerHTML = html;

		fragment.querySelectorAll( 'span.atwho-query' ).forEach( ( node ) => {
			node.replaceWith( node.textContent || '' );
		} );

		fragment.querySelectorAll( 'img.emojioneemoji, img.emoji' ).forEach( ( node ) => {
			const emoji = node.dataset.emojiChar || node.getAttribute( 'alt' ) || '';
			node.replaceWith( emoji );
		} );

		return fragment.innerHTML;
	};

	const normalizeForumHtmlText = ( html ) => {
		const fragment = document.createElement( 'div' );
		fragment.innerHTML = getForumEditorHtml( html );

		return ( fragment.textContent || '' ).replace( /\u00a0/g, ' ' ).trim();
	};

	const hasTopicLinkPreview = ( form ) => {
		const input = form.querySelector( '#link_preview_data' );

		if ( ! input || ! input.value ) {
			return false;
		}

		try {
			const data = JSON.parse( input.value );
			return !! ( data && data.link_url );
		} catch ( error ) {
			return false;
		}
	};

	const syncTopicFormState = ( form ) => {
		if ( ! form ) {
			return;
		}

		const title = form.querySelector( '#bbp_topic_title' );
		const editor = form.querySelector( '.bbp_editor_topic_content' );
		const content = form.querySelector( '#bbp_topic_content' );
		const editorHtml = editor ? getForumEditorHtml( editor.innerHTML ) : '';

		if ( content && editorHtml ) {
			content.value = editorHtml;
		}

		const hasTitle = !! ( title && title.value.trim() );
		const hasContent = !! (
			( content && normalizeForumHtmlText( content.value ) ) ||
			normalizeForumHtmlText( editorHtml )
		);

		form.classList.toggle( 'has-title', hasTitle );
		form.classList.toggle( 'has-content', hasContent );
		form.classList.toggle( 'has-link-preview', hasTopicLinkPreview( form ) );
	};

	const syncAllTopicForms = () => {
		document.querySelectorAll( '.bbp-topic-form form#new-post' ).forEach( syncTopicFormState );
	};

	const getReplyIdFromLink = ( link ) => {
		const replyIdClass = Array.from( link.classList ).find( ( className ) => /^\d+$/.test( className ) );

		if ( replyIdClass ) {
			return replyIdClass;
		}

		try {
			return new URL( link.href, window.location.href ).searchParams.get( 'bbp_reply_to' ) || '0';
		} catch ( error ) {
			return '0';
		}
	};

	const getForumReplyModal = ( link ) => {
		const inlineId = link.getAttribute( 'data-modal-id-inline' );

		if ( inlineId ) {
			return document.getElementById( inlineId );
		}

		const modalClass = link.getAttribute( 'data-modal-id' );
		const scope = link.closest( '#bbpress-forums' ) || document;

		if ( ! modalClass ) {
			return null;
		}

		return scope.querySelector( `.${ modalClass }` ) || document.querySelector( `#bbpress-forums .${ modalClass }` );
	};

	const closeForumReplyModal = () => {
		if ( ! window.jQuery ) {
			return false;
		}

		const $ = window.jQuery;
		const $openModals = $( '.bb-modal-box.bb-modal-open, #bbpress-forums .bbp-reply-form.bb-modal' );

		$openModals.fadeOut( 50 ).removeClass( 'bb-modal-open' );
		$( '.bb-modal-overlay' ).fadeOut( 50, function () {
			$( this ).remove();
		} );

		document.body.classList.remove( 'popup-modal-reply', 'bb-modal-overlay-open' );
		document.querySelectorAll( '.bs-reply-list-item.in-focus' ).forEach( ( item ) => item.classList.remove( 'in-focus' ) );

		$( '.bbp-reply-form .js-modal-close' ).trigger( 'bbp_after_close_topic_reply_form', { click_event: null } );
		$( document ).trigger( 'bbp_after_close_topic_reply_form_on_overlay', { click_event: null } );

		return true;
	};

	const ensureForumReplyOverlay = () => {
		if ( ! document.querySelector( '.bb-modal-overlay' ) ) {
			document.body.insertAdjacentHTML( 'beforeend', '<div class="bb-modal-overlay js-modal-close"></div>' );
		}

		document.body.classList.add( 'bb-modal-overlay-open', 'popup-modal-reply' );
		window.jQuery( '.bb-modal-overlay' ).fadeTo( 0, 1 );
	};

	const updateForumReplyModalContext = ( modal, link ) => {
		const replyItem = link.closest( '.bs-reply-list-item' );
		const topicLead = document.querySelector( '#bbpress-forums .topic-lead' );
		const form = modal.querySelector( 'form#new-post' );
		const replyToInput = form ? form.querySelector( '#bbp_reply_to' ) : null;
		const replyToUser = modal.querySelector( '#bbp-reply-to-user' );
		const excerptBlock = modal.querySelector( '#bbp-reply-exerpt' );

		if ( replyToInput ) {
			replyToInput.value = link.hasAttribute( 'data-modal-id-inline' ) ? getReplyIdFromLink( link ) : '0';
		}

		if ( replyToUser ) {
			const author = replyItem ?
				replyItem.querySelector( '.item-meta .bbp-author-name' ) :
				topicLead && topicLead.querySelector( '.item-meta .bbp-author-name' );

			if ( author ) {
				replyToUser.textContent = author.textContent.trim();
			}
		}

		if ( excerptBlock ) {
			const content = replyItem ?
				replyItem.querySelector( ':scope > .bbp-reply-content' ) :
				topicLead && topicLead.querySelector( '.item-description' );
			const excerpt = content ? ( content.textContent || '' ).replace( /\s+/g, ' ' ).trim().slice( 0, 50 ) : '';

			excerptBlock.classList.toggle( 'show-empty', ! excerpt );
			excerptBlock.textContent = excerpt ? `${ excerpt }...` : '';
		}
	};

	const openForumReplyModal = ( link ) => {
		if (
			! window.jQuery ||
			! link.matches( '.bbp-reply-to-link[data-modal-id-inline], .bbp-topic-reply-link[data-modal-id], .bbp-reply-to-link[data-modal-id]' )
		) {
			return false;
		}

		const $ = window.jQuery;
		const modal = getForumReplyModal( link );

		if ( ! modal ) {
			return false;
		}

		ensureForumReplyOverlay();
		updateForumReplyModalContext( modal, link );
		$( modal ).fadeIn( 0 ).addClass( 'bb-modal-open' );
		$( modal ).trigger(
			link.hasAttribute( 'data-modal-id-inline' ) ? 'bbp_after_load_inline_reply_form' : 'bbp_after_load_reply_form',
			{ click_event: link }
		);

		const editor = modal.querySelector( '.bbp-the-content[contenteditable="true"], textarea.bbp-the-content' );
		if ( editor ) {
			window.setTimeout( () => editor.focus(), 0 );
		}

		return true;
	};

	document.addEventListener( 'click', ( event ) => {
		const target = event.target instanceof Element ? event.target : null;
		const closeTarget = target && target.closest( '.js-modal-close, .bb-modal-overlay' );
		const discardTarget = target && target.closest( '.bb_discard_topic_reply_draft' );

		if ( closeTarget || discardTarget ) {
			if ( closeTarget ) {
				event.preventDefault();
			}
			closeForumReplyModal();
			return;
		}

		const link = target ? target.closest( '.bbp-reply-to-link[data-modal-id-inline], .bbp-topic-reply-link[data-modal-id], .bbp-reply-to-link[data-modal-id]' ) : null;

		if ( ! link || ! openForumReplyModal( link ) ) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		event.stopImmediatePropagation();
	}, true );

	if ( window.jQuery ) {
		window.jQuery( document ).on( 'bbp_after_submit_reply_form', '.bbp-reply-form', function ( event, data ) {
			if ( ! data || ! data.response || data.response.success ) {
				closeForumReplyModal();
			}
		} );
	}

	const scheduleTopicFormSync = ( target ) => {
		if ( ! ( target instanceof Element ) ) {
			return;
		}

		const form = target.closest( '.bbp-topic-form form#new-post' );
		if ( ! form ) {
			return;
		}

		window.requestAnimationFrame( () => syncTopicFormState( form ) );
	};

	document.addEventListener( 'input', ( event ) => {
		scheduleTopicFormSync( event.target );
	} );

	document.addEventListener( 'change', ( event ) => {
		scheduleTopicFormSync( event.target );
	} );

	document.addEventListener( 'keyup', ( event ) => {
		scheduleTopicFormSync( event.target );
	} );

	document.addEventListener( 'submit', ( event ) => {
		const form = event.target;

		if ( form instanceof HTMLFormElement && form.matches( '.bbp-topic-form form#new-post' ) ) {
			syncTopicFormState( form );
		}
	} );

	if ( 'MutationObserver' in window && document.body ) {
		const topicFormObserver = new MutationObserver( ( records ) => {
			const shouldSync = records.some( ( record ) => Array.from( record.addedNodes ).some( ( node ) => {
				return node instanceof Element && (
					node.matches( '.bbp-topic-form' ) ||
					!! node.querySelector( '.bbp-topic-form' )
				);
			} ) );

			if ( shouldSync ) {
				syncAllTopicForms();
			}
		} );

		topicFormObserver.observe( document.body, {
			childList: true,
			subtree:   true,
		} );
	}

	syncAllTopicForms();

} );
